import { useEffect, useCallback, useState } from 'react';
import { atom, selector, useRecoilState } from 'recoil';
import {
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router-dom';
import AuthApi from 'libs/api/auth';
import AuthService from 'services/Auth';
import pagesConstant from 'constants/pages';
import qs from 'qs';

const api = new AuthApi();

const defaultState = {
  loading: false,
  dashboard: [],
  shopping_centers: [],
  companies: [],
  company_categories: [],
  user: {},
};

const userState = atom({
  key: 'userState',
  default: defaultState,
});

const currentPageState = atom({
  key: 'currentPageState',
  default: {},
});

const pagesState = atom({
  key: 'pageState',
  default: pagesConstant,
});

export const getPage = selector({
  key: 'getPage',
  get: ({ get }) => {
    return get(currentPageState);
  },
});

export const getDashboard = selector({
  key: 'userDashboard',
  get: ({ get }) => {
    const user = get(userState);

    return user.dashboard;
  },
});

export const getUser = selector({
  key: 'getUser',
  get: ({ get }) => {
    const user = get(userState);

    return user.user;
  },
});

export const getShoppingCenters = selector({
  key: 'getShoppingCenters',
  get: ({ get }) => {
    const user = get(userState);

    return user.shopping_centers;
  },
});

export const getCompanies = selector({
  key: 'getCompanies',
  get: ({ get }) => {
    const user = get(userState);

    return user.companies;
  },
});

export const getCompanyCategories = selector({
  key: 'getCompanyCategories',
  get: ({ get }) => {
    const user = get(userState);

    return user.company_categories;
  },
});

export const getUserLoadingState = selector({
  key: 'getUserLoadingState', // unique ID (with respect to other atoms/selectors)
  get: ({ get }) => {
    const user = get(userState);

    return user.loading;
  },
});

export const getPages = selector({
  key: 'getPages', // unique ID (with respect to other atoms/selectors)
  get: ({ get }) => {
    return get(pagesState);
  },
});

const useUser = () => {
  const [user, setUser] = useRecoilState(userState);
  const [currentPage, setCurrentPage] = useRecoilState(currentPageState);
  const [changePasswordErrors, setChangePasswordErrors] = useState([]);
  const [changePasswordIsOpen, setChangePasswordIsOpen] = useState(false);
  const [, setPages] = useRecoilState(pagesState);
  const navigate = useNavigate();
  const { pathname = '' } = useLocation();
  const [search] = useSearchParams();
  const changeCurrentUserPassword = useCallback(
    async (data) => {
      setUser((s) => ({ ...s, loading: true }));
      try {
        await api.updatePassword({ ...data }, user?.user?.id);
        setChangePasswordIsOpen(false);
      } catch (e) {
        console.log(e);
        setChangePasswordErrors(e?.response?.data || []);
      } finally {
        setUser((s) => ({ ...s, loading: false }));
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    },
    [user],
  );

  useEffect(() => {
    (async () => {
      if (AuthService.getToken()) {
        if (!user?.user?.id) {
          setUser((s) => ({ ...s, loading: true }));
          try {
            const { data } = await api.me();
            setUser({ ...data, loading: false });
          } catch (e) {
            console.log('e=', e);
            setUser(defaultState);
            if (e?.response?.status === 401) {
              AuthService.logout();
              navigate('/login');
            }
          }
        }
      } else {
        navigate(
          `/login?redirectUrl=${pathname}${search ? `&${qs.stringify(Object.fromEntries([...search]))}` : ''
          }`,
        );
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname, search]);

  useEffect(() => {
    const { dashboard = [] } = user;
    const filteredPages = [];

    pagesConstant.forEach((i) => {
      const find = dashboard.find((m) => m.slug === i.slug);
      if (find) {
        const currentLinks = i.subLinks;
        const availableLinks = [];
        if (currentLinks) {
          currentLinks.forEach((i) => {
            const availableLink = find.links.find((f) => f.slug === i.slug);
            const additionalProps = {};
            if (i.slug === 'my_priority' && availableLink) {
              additionalProps.link = availableLink.link;
            }
            if (availableLink) {
              availableLinks.push({
                ...i,
                ability: { ...(availableLink.ability || {}) },
                filters: [...(availableLink.filters || [])],
                ...additionalProps,
              });
            }
          });
        }
        filteredPages.push({
          ...i,
          subLinks: availableLinks,
          ...(find || {}),
        });
      }
    });

    const careerObject = filteredPages.find((page) => page.title === 'Career');
    const retailAcademyObject = filteredPages.find(
      (page) => page.title === 'Retail academy',
    );

    if (careerObject) {
      filteredPages.splice(filteredPages.indexOf(careerObject), 1);
    }
    if (retailAcademyObject) {
      filteredPages.splice(filteredPages.indexOf(retailAcademyObject), 1);
    }

    if (careerObject) {
      filteredPages.push(careerObject);
    }
    if (retailAcademyObject) {
      filteredPages.push(retailAcademyObject);
    }

    setPages(filteredPages);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user.dashboard, pathname]);

  const logut = () => {
    setUser(defaultState);
    setCurrentPage({});
    setPages([]);

  };

  return {
    user,
    changeCurrentUserPassword,
    currentPage,
    setCurrentPage,
    changePasswordErrors,
    setChangePasswordErrors,
    setChangePasswordIsOpen,
    changePasswordIsOpen,
    logut,
  };
};

export default useUser;
